<template>
	<div class="course-modules-page">
		<!-- Header -->
		<HeaderSubsection />

		<!-- Main Content -->
		<div class="course-modules-container">
			<!-- Notification -->
			<transition name="fade">
				<div
					v-if="notification.show"
					class="notification"
					:class="`notification--${notification.type}`"
				>
					{{ notification.message }}
				</div>
			</transition>

			<!-- Breadcrumbs -->
			<nav class="breadcrumbs" aria-label="Навигация">
				<span class="breadcrumbs__link" @click="goToDashboard">
					<svg class="breadcrumbs__icon" viewBox="0 0 20 20" fill="currentColor">
						<path
							d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"
						/>
					</svg>
					Главная
				</span>
				<svg class="breadcrumbs__separator" viewBox="0 0 20 20" fill="currentColor">
					<path
						fill-rule="evenodd"
						d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
						clip-rule="evenodd"
					/>
				</svg>
				<span class="breadcrumbs__link" @click="goToCourses">Мои курсы</span>
				<svg class="breadcrumbs__separator" viewBox="0 0 20 20" fill="currentColor">
					<path
						fill-rule="evenodd"
						d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
						clip-rule="evenodd"
					/>
				</svg>
				<span class="breadcrumbs__link" @click="goToCourse">
					Курс: {{ courseName }}
				</span>
				<svg class="breadcrumbs__separator" viewBox="0 0 20 20" fill="currentColor">
					<path
						fill-rule="evenodd"
						d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
						clip-rule="evenodd"
					/>
				</svg>
				<span class="breadcrumbs__current">
					{{ isModuleView ? moduleTitle : 'Модули' }}
				</span>
			</nav>

			<!-- Course Info -->
			<div v-if="!isModuleView" class="course-info">
				<h1 class="course-info__title">{{ courseName }}</h1>
				<p class="course-info__subtitle">Управление модулями курса</p>
			</div>

			<!-- Module View -->
			<section v-if="isModuleView" class="module-view">
				<div class="module-hero">
					<div class="module-card-large">
						<div
							class="module-card-cover"
							:style="{
								backgroundImage: moduleCover ? `url(${moduleCover})` : 'none',
							}"
						>
							<div class="module-card-cover__overlay"></div>
							<button class="module-manage-btn" @click="goToModuleManagement">
								Управление
							</button>
						</div>
						<div class="module-card-title">{{ moduleTitle }}</div>
					</div>
				</div>

				<div class="module-tabs">
					<button
						v-for="tab in moduleTabs"
						:key="tab.id"
						:class="['module-tab', { 'module-tab--active': activeTab === tab.id }]"
						type="button"
						@click="activeTab = tab.id"
					>
						<span class="module-tab__icon" aria-hidden="true">
							<svg
								v-if="tab.id === 'homework'"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M8 5H6a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M8 3h8v4H8V3Z"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M8 12h8M8 16h6"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
							<svg
								v-else-if="tab.id === 'tests'"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M9 12l2 2 4-4M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6Z"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
						</span>
						<span class="module-tab__label">{{ tab.label }}</span>
					</button>
				</div>

				<div class="module-tab-content">
					<div v-if="activeTab === 'homework'" class="homework-panel">
						<div v-if="moduleHomeworks.length === 0" class="panel-empty">
							Домашние задания пока не созданы.
						</div>
						<div v-else class="panel-cards">
							<div
								v-for="homework in moduleHomeworks"
								:key="homework.id"
								class="module-item-card module-item-card--homework"
							>
								<div class="module-item-card__header">
									<h4 class="module-item-card__title">
										{{ homework.title || homework.name || 'Домашнее задание' }}
									</h4>
									<a
										class="module-item-card__link"
										@click.prevent="goToHomeworkReview(homework)"
										href="#"
									>
										Проверка ДЗ
									</a>
								</div>
								<div class="module-item-card__meta module-item-card__meta--bottom">
									<span>Срок сдачи: {{ formatDeadline(homework) }}</span>
									<span class="module-item-card__submitted">
										{{ formatSubmitted(homework, 'homework') }}
									</span>
								</div>
							</div>
						</div>
					</div>

					<div v-if="activeTab === 'tests'" class="tests-panel">
						<div v-if="moduleTests.length === 0" class="panel-empty">
							Тесты пока не созданы.
						</div>
						<div v-else class="panel-cards">
							<div
								v-for="test in moduleTests"
								:key="test.id"
								class="module-item-card module-item-card--test"
							>
								<div class="module-item-card__header">
									<h4 class="module-item-card__title">
										{{ test.title || 'Тест' }}
									</h4>
									<a
										class="module-item-card__link"
										@click.prevent="goToTestReview(test)"
										href="#"
									>
										Проверка теста
									</a>
								</div>
								<div class="module-item-card__meta module-item-card__meta--pairs">
									<span>Вопросов: {{ test.questions_count ?? test.questions?.length ?? 0 }}</span>
									<span>Попыток: {{ test.max_attempts ?? test.attempts ?? 0 }}</span>
								</div>
								<div class="module-item-card__meta module-item-card__meta--pairs">
									<span>
										Время прохождения:
										{{ test.duration_minutes ?? test.time_limit_minutes ?? test.duration ?? 0 }} минут
									</span>
									<span>Проходной балл: {{ test.passing_score ?? test.passingScore ?? 0 }}</span>
								</div>
								<div class="module-item-card__meta module-item-card__meta--bottom">
									<span class="module-item-card__submitted">
										{{ formatSubmitted(test, 'tests') }}
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<!-- Modules List or Form -->
			<div v-if="!isModuleView && !showModuleForm && !showLessonsList">
					<ModulesList
						:modules="modules"
						:loading="loading"
						:load-error="modulesLoadError"
						@create-module="openModuleForm()"
						@retry-load="loadModules"
						@edit-module="openModuleForm"
					@delete-module="deleteModule"
					@publish-module="publishModule"
					@unpublish-module="unpublishModule"
					@view-lessons="viewLessons"
					@reorder-modules="reorderModules"
				/>
			</div>

			<!-- Module Form -->
			<div v-if="!isModuleView && showModuleForm">
				<ModuleForm
					:module="currentModule"
					:module-tests="moduleFormTests"
					:saving="saving"
					@submit="saveModule"
					@cancel="closeModuleForm"
				/>
			</div>

			<!-- Lessons List -->
			<div v-if="!isModuleView && showLessonsList">
				<LessonsList
					:lessons="lessons"
					:module-name="currentModule?.title"
					:loading="loadingLessons"
					@back="closeLessonsList"
					@create-lesson="openLessonForm()"
					@edit-lesson="openLessonForm"
					@delete-lesson="deleteLesson"
					@view-lesson="viewLesson"
					@reorder-lessons="reorderLessons"
				/>
			</div>

			<!-- Lesson Form Modal -->
			<div
				v-if="!isModuleView && showLessonForm"
				class="modal-overlay"
				@click.self="closeLessonForm"
			>
				<div class="modal-content">
					<LessonForm
						:lesson="currentLesson"
						:saving="saving"
						@submit="saveLesson"
						@cancel="closeLessonForm"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import HeaderSubsection from './TeacherDashboard/DivWrapperScreen/sections/HeaderSubsection.vue'
import ViewSubsection from './TeacherDashboard/DivWrapperScreen/sections/ViewSubsection.vue'
import ModulesList from '@/components/course/ModulesList.vue'
import ModuleForm from '@/components/course/ModuleForm.vue'
import LessonsList from '@/components/course/LessonsList.vue'
import LessonForm from '@/components/course/LessonForm.vue'
import {
	coursesService,
	modulesService,
	lessonsService,
	testsService,
	homeworkService,
} from '@/services'

export default {
	name: 'CourseModules',
	components: {
		HeaderSubsection,
		ViewSubsection,
		ModulesList,
		ModuleForm,
		LessonsList,
		LessonForm,
	},
	data() {
		return {
			courseId: null,
			courseName: '',
			modules: [],
			lessons: [],
			currentModule: null,
			currentLesson: null,
			selectedModuleId: null,
			selectedModule: null,
			courseStudentsCount: 0,
			moduleHomeworks: [],
			moduleTests: [],
			moduleFormTests: [],
			activeTab: 'homework',
			moduleTabs: [
				{ id: 'homework', label: 'Домашние задания' },
				{ id: 'tests', label: 'Тесты' },
			],
			loading: true, // Сразу true чтобы не показывать "Создать модуль" до загрузки
				modulesLoadError: '',
			loadingLessons: false,
			saving: false,
			showModuleForm: false,
			showLessonsList: false,
			showLessonForm: false,

			// уведомления
			notification: {
				show: false,
				message: '',
				type: 'success', // 'success' | 'error'
			},

			_notificationTimeoutId: null,
		}
	},
	async mounted() {
		this.courseId = this.$route.params.courseId || this.$route.params.id
		if (!this.courseId) {
			this.showNotification('ID курса не указан', 'error', 4000)
			this.$router.push('/teacher/dashboard')
			return
		}
		this.selectedModuleId = this.$route.query?.moduleId || null
		await Promise.all([this.loadCourse(), this.loadModules()])
		if (this.selectedModuleId) {
			await this.loadModuleDetails(this.selectedModuleId)
		}
	},
	watch: {
		'$route.query.moduleId'(newId) {
			this.selectedModuleId = newId || null
			if (this.selectedModuleId) {
				this.resetModuleView()
				this.loadModuleDetails(this.selectedModuleId)
			} else {
				this.resetModuleView()
			}
		},
	},
	computed: {
		isModuleView() {
			return !!this.selectedModuleId
		},
		moduleTitle() {
			if (!this.selectedModule) return 'Модуль'
			const moduleIndex = this.getModuleNumber(this.selectedModule)
			const title = this.selectedModule.title || 'Без названия'
			return `Модуль ${moduleIndex}: ${title}`
		},
		moduleCover() {
			const cover =
				this.selectedModule?.cover_image_url ||
				this.selectedModule?.cover_image ||
				this.selectedModule?.coverImage ||
				''
			if (!cover) return ''
			if (cover.startsWith('http')) return cover
			return cover.startsWith('/') ? cover : `/${cover}`
		},
	},
	methods: {
		// Показ уведомления
		showNotification(message, type = 'success', timeout = 3000) {
			// очищаем предыдущий таймер, если был
			if (this._notificationTimeoutId) {
				clearTimeout(this._notificationTimeoutId)
				this._notificationTimeoutId = null
			}

			this.notification.message = message
			this.notification.type = type
			this.notification.show = true

			this._notificationTimeoutId = setTimeout(() => {
				this.notification.show = false
				this._notificationTimeoutId = null
			}, timeout)
		},

		// Navigation
		goToDashboard() {
			this.$router.push('/teacher/dashboard')
		},
		goToCourses() {
			this.$router.push('/teacher/dashboard').then(() => {
				this.$nextTick(() => {
					setTimeout(() => {
						const element = document.getElementById('teacher-courses')
						if (element) {
							element.scrollIntoView({
								behavior: 'smooth',
								block: 'start',
							})
						}
					}, 100)
				})
			})
		},
		goToCourse() {
			this.$router.push(`/teacher/courses/${this.courseId}`)
		},
		goToModuleManagement(_tab = 'modules') {
			if (!this.courseId) return
			this.$router.push({
				path: `/teacher/courses/${this.courseId}/content`,
				query: {
					from: 'module-view',
					tab: 'modules',
					moduleId: this.selectedModuleId,
					moduleTitle: this.moduleTitle,
				},
			})
		},

		// Load Data
		async loadCourse() {
			try {
				const course = await coursesService.getById(this.courseId)
				this.courseName = course.title
				const studentsResponse = await coursesService.getStudents(this.courseId)
				const studentsList = Array.isArray(studentsResponse)
					? studentsResponse
					: studentsResponse?.students || []
				this.courseStudentsCount = studentsList.length || 0
			} catch (error) {
				console.error('Ошибка при загрузке курса:', error)
				this.showNotification('Не удалось загрузить данные курса', 'error', 5000)
			}
			},

			async loadModules() {
				try {
					this.loading = true
					this.modulesLoadError = ''
					const response = await modulesService.getByCourseId(this.courseId)
					this.modules = Array.isArray(response)
						? response
						: Array.isArray(response?.modules)
							? response.modules
							: []
				} catch (error) {
					console.error('Ошибка при загрузке модулей:', error)
					this.modules = []
					this.modulesLoadError =
						error?.message || 'Не удалось загрузить модули курса'
					this.showNotification('Не удалось загрузить модули', 'error', 5000)
				} finally {
					this.loading = false
				}
			},

		resetModuleView() {
			this.selectedModule = null
			this.moduleHomeworks = []
			this.moduleTests = []
			this.activeTab = 'homework'
		},

		getModuleNumber(module) {
			return (
				module.order_number ||
				module.order_index ||
				module.order ||
				this.modules.findIndex(item => item.id === module.id) + 1 ||
				1
			)
		},

		async loadModuleDetails(moduleId) {
			if (!moduleId) return
			try {
				this.loading = true
				const [moduleData, homeworksData, testsData] = await Promise.allSettled([
					modulesService.getById(moduleId),
					homeworkService.getByModuleId(moduleId, true),
					testsService.getByModuleId(moduleId, true),
				])

				if (moduleData.status === 'fulfilled') {
					this.selectedModule = moduleData.value
				} else {
					this.selectedModule = null
				}

				this.moduleHomeworks =
					homeworksData.status === 'fulfilled' && Array.isArray(homeworksData.value)
						? homeworksData.value
						: []
				this.moduleTests =
					testsData.status === 'fulfilled' && Array.isArray(testsData.value)
						? testsData.value
						: []

				await this.loadSubmissionCounts()
			} catch (error) {
				console.error('Ошибка при загрузке данных модуля:', error)
				this.showNotification('Не удалось загрузить данные модуля', 'error', 4000)
			} finally {
				this.loading = false
			}
		},

		async loadSubmissionCounts() {
			const homeworkPromises = this.moduleHomeworks.map(async homework => {
				const response = await homeworkService
					.getSubmissions(homework.id)
					.catch(() => null)
				const list = Array.isArray(response)
					? response
					: response?.submissions || response?.data || []
				homework._submittedCount = Array.isArray(list) ? list.length : 0
			})

			const testPromises = this.moduleTests.map(async test => {
				const response = await testsService.getAttempts(test.id).catch(() => null)
				const list = Array.isArray(response)
					? response
					: response?.attempts || response?.data || []
				if (!Array.isArray(list)) {
					test._submittedCount = 0
					return
				}
				const uniqueStudents = new Set(
					list
						.map(item => item?.student_id || item?.studentId || item?.user_id || item?.userId)
						.filter(Boolean)
				)
				test._submittedCount = uniqueStudents.size || list.length
			})

			await Promise.allSettled([...homeworkPromises, ...testPromises])
		},

		async loadLessons(moduleId) {
			try {
				this.loadingLessons = true
				this.lessons = await lessonsService.getByModuleId(moduleId)
			} catch (error) {
				console.error('Ошибка при загрузке уроков:', error)
				this.showNotification('Не удалось загрузить уроки', 'error', 5000)
			} finally {
				this.loadingLessons = false
			}
		},

		// Module Actions
		async openModuleForm(module = null) {
			this.currentModule = module
			this.showModuleForm = true
			const moduleId = module?.id
			if (moduleId) {
				await this.loadModuleFormTests(moduleId)
			} else {
				this.moduleFormTests = []
			}
		},

		closeModuleForm() {
			this.currentModule = null
			this.showModuleForm = false
			this.moduleFormTests = []
		},

		async loadModuleFormTests(moduleId) {
			try {
				const response = await testsService.getByModuleId(moduleId, true)
				this.moduleFormTests = Array.isArray(response)
					? response
					: response?.tests || response?.data || []
			} catch (error) {
				console.error('Ошибка загрузки тестов:', error)
				this.moduleFormTests = []
				this.showNotification('Не удалось загрузить тесты', 'error', 4000)
			}
		},

		async saveModule(moduleData) {
			try {
				this.saving = true

				if (this.currentModule) {
					// Update
					await modulesService.update(this.currentModule.id, moduleData)
					this.showNotification('Модуль успешно обновлён!', 'success')
				} else {
					// Create
					await modulesService.create(this.courseId, moduleData)
					this.showNotification('Модуль успешно создан!', 'success')
				}

				await this.loadModules()
				this.closeModuleForm()
			} catch (error) {
				console.error('Ошибка при сохранении модуля:', error)
				this.showNotification(
					'Ошибка при сохранении модуля: ' + error.message,
					'error',
					5000
				)
			} finally {
				this.saving = false
			}
		},

		async deleteModule(module) {
			try {
				await modulesService.delete(module.id)
				this.showNotification('Модуль успешно удалён!', 'success')
				await this.loadModules()
			} catch (error) {
				console.error('Ошибка при удалении модуля:', error)
				this.showNotification(
					'Ошибка при удалении модуля: ' + error.message,
					'error',
					5000
				)
			}
		},

		async publishModule(module) {
			try {
				await modulesService.publish(module.id)
				this.showNotification('Модуль успешно опубликован!', 'success')
				await this.loadModules()
			} catch (error) {
				console.error('Ошибка при публикации модуля:', error)
				this.showNotification(
					'Ошибка при публикации модуля: ' + error.message,
					'error',
					5000
				)
			}
		},

		async unpublishModule(module) {
			try {
				await modulesService.unpublish(module.id)
				this.showNotification('Модуль снят с публикации!', 'success')
				await this.loadModules()
			} catch (error) {
				console.error('Ошибка:', error)
				this.showNotification('Ошибка: ' + error.message, 'error', 5000)
			}
		},

		async reorderModules(moduleIds) {
			try {
				await modulesService.reorder(this.courseId, moduleIds)
			} catch (error) {
				console.error('Ошибка при изменении порядка:', error)
				this.showNotification(
					'Ошибка при изменении порядка модулей: ' + error.message,
					'error',
					5000
				)
				await this.loadModules()
			}
		},

		// Lessons Actions
		async viewLessons(module) {
			this.currentModule = module
			this.showLessonsList = true
			await this.loadLessons(module.id)
		},

		closeLessonsList() {
			this.currentModule = null
			this.showLessonsList = false
			this.lessons = []
		},

		openLessonForm(lesson = null) {
			this.currentLesson = lesson
			this.showLessonForm = true
		},

		closeLessonForm() {
			this.currentLesson = null
			this.showLessonForm = false
		},

		async saveLesson(lessonData) {
			try {
				this.saving = true

				if (this.currentLesson) {
					// Update
					await lessonsService.update(this.currentLesson.id, lessonData)
					this.showNotification('Урок успешно обновлён!', 'success')
				} else {
					// Create
					await lessonsService.create(this.currentModule.id, lessonData)
					this.showNotification('Урок успешно создан!', 'success')
				}

				await this.loadLessons(this.currentModule.id)
				this.closeLessonForm()
			} catch (error) {
				console.error('Ошибка при сохранении урока:', error)
				this.showNotification(
					'Ошибка при сохранении урока: ' + error.message,
					'error',
					5000
				)
			} finally {
				this.saving = false
			}
		},

		async deleteLesson(lesson) {
			try {
				await lessonsService.delete(lesson.id)
				this.showNotification('Урок успешно удалён!', 'success')
				await this.loadLessons(this.currentModule.id)
			} catch (error) {
				console.error('Ошибка при удалении урока:', error)
				this.showNotification(
					'Ошибка при удалении урока: ' + error.message,
					'error',
					5000
				)
			}
		},

		viewLesson(lesson) {
			// TODO: Implement lesson preview
			console.log('View lesson:', lesson)
		},

		async reorderLessons(lessonIds) {
			try {
				await lessonsService.reorder(this.currentModule.id, lessonIds)
			} catch (error) {
				console.error('Ошибка при изменении порядка:', error)
				this.showNotification(
					'Ошибка при изменении порядка уроков: ' + error.message,
					'error',
					5000
				)
				await this.loadLessons(this.currentModule.id)
			}
		},

		formatDeadline(item) {
			const dateValue =
				item?.deadline ||
				item?.due_date ||
				item?.dueDate ||
				item?.deadline_at ||
				item?.deadlineAt ||
				item?.due_at ||
				item?.dueAt ||
				item?.ends_at ||
				item?.endsAt ||
				null
			const timeValue =
				item?.due_time ||
				item?.dueTime ||
				item?.deadline_time ||
				item?.deadlineTime ||
				item?.ends_time ||
				item?.endsTime ||
				null
			if (!dateValue && !timeValue) return 'Не указан'
			const date = dateValue ? new Date(dateValue) : null
			const dateLabel = date
				? date.toLocaleDateString('ru-RU', {
						day: '2-digit',
						month: '2-digit',
						year: '2-digit',
					})
				: ''
			if (!timeValue) return dateLabel
			const timeParts = String(timeValue).split(':')
			const timeLabel =
				timeParts.length >= 2
					? `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}`
					: String(timeValue)
			return `${dateLabel} ${timeLabel}`
		},

		formatAutoPublish(item) {
			const dateValue =
				item?.auto_publish_at ||
				item?.autoPublishAt ||
				item?.scheduled_publish_at ||
				item?.scheduledPublishAt ||
				null
			if (!dateValue) return 'Не задана'
			const date = new Date(dateValue)
			const dateLabel = date.toLocaleDateString('ru-RU', {
				day: '2-digit',
				month: '2-digit',
				year: '2-digit',
			})
			const timeLabel = date.toLocaleTimeString('ru-RU', {
				hour: '2-digit',
				minute: '2-digit',
			})
			return `${dateLabel} ${timeLabel}`
		},

		formatSubmitted(item, type) {
			const submitted =
				item?._submittedCount ??
				item?.submitted_count ??
				item?.submissions_count ??
				item?.submittedCount ??
				item?.submissionsCount ??
				0
			const total =
				item?.students_count ??
				item?.studentsCount ??
				item?.participants ??
				item?.students ??
				this.courseStudentsCount ??
				0
			if (total) {
				return `Сдали: ${submitted}/${total}`
			}
			return type === 'tests' ? `Сдали: ${submitted}` : `Сдали: ${submitted}`
		},

		goToHomeworkReview(homework) {
			if (!homework?.id) return
			this.$router.push({
				name: 'TeacherHomeworkReview',
				params: { homeworkId: homework.id },
				query: {
					from: 'module-view',
					courseId: this.courseId,
					courseTitle: this.courseName,
					moduleId: this.selectedModuleId,
					moduleTitle: this.moduleTitle,
				},
			})
		},

		goToTestReview(test) {
			if (!test?.id) return
			this.$router.push({
				name: 'TeacherTestReview',
				params: { testId: test.id },
				query: {
					from: 'module-view',
					courseId: this.courseId,
					courseTitle: this.courseName,
					moduleId: this.selectedModuleId,
					moduleTitle: this.moduleTitle,
				},
			})
		},

		toggleHomeworkVisibility(homework) {
			// TODO: добавить API для скрытия/показа
			homework.is_hidden = !homework.is_hidden
		},

		toggleTestVisibility(test) {
			// TODO: добавить API для скрытия/показа
			test.is_hidden = !test.is_hidden
		},
	},
}
</script>

<style scoped>
.course-modules-page {
	background-color: #ffffff;
	min-height: 100vh;
	width: 100%;
	display: flex;
	flex-direction: column;
}

.course-modules-container {
	flex: 1;
	max-width: 1400px;
	width: 100%;
	margin: 0 auto;
	padding: 40px 20px;
}

/* Notification */
.notification {
	position: fixed;
	top: 20px;
	right: 20px;
	padding: 12px 20px;
	border-radius: 8px;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
	z-index: 2000;
	color: #ffffff;
}

.notification--success {
	background-color: #28a745;
}

.notification--error {
	background-color: #dc3545;
}

/* fade transition */
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

/* Breadcrumbs */
.breadcrumbs {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 10px 20px;
	background: rgba(255, 255, 255, 0.85);
	backdrop-filter: blur(20px);
	border: 1px solid rgba(255, 255, 255, 0.18);
	border-radius: 9999px;
	margin-bottom: 32px;
	box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
	font-family: 'Poppins', Helvetica;
}

.breadcrumbs__link {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	color: #475569;
	text-decoration: none;
	font-size: 13px;
	font-weight: 500;
	transition: all 0.2s ease;
	padding: 4px 8px;
	border-radius: 8px;
	cursor: pointer;
}

.breadcrumbs__link:hover {
	color: #0095ec;
	background: rgba(0, 149, 236, 0.1);
}

.breadcrumbs__icon {
	width: 14px;
	height: 14px;
}

.breadcrumbs__separator {
	width: 16px;
	height: 16px;
	color: #94a3b8;
	opacity: 0.5;
}

.breadcrumbs__current {
	color: #ffffff;
	font-weight: 600;
	font-size: 13px;
	white-space: normal;
	padding: 4px 12px;
	background: #0095ec;
	border-radius: 9999px;
}

/* Course Info */
.course-info {
	margin-bottom: 32px;
}

.course-info__title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 32px;
	font-weight: 700;
	margin: 0 0 8px 0;
}

.course-info__subtitle {
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 16px;
	margin: 0;
}

/* Modal */
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
	padding: 20px;
}

.modal-content {
	max-width: 800px;
	width: 100%;
	max-height: 90vh;
	overflow-y: auto;
}

/* Module View */
.module-view {
	display: flex;
	flex-direction: column;
	gap: 28px;
	margin-bottom: 32px;
}

.module-hero {
	display: flex;
	justify-content: center;
}

.module-card-large {
	width: 100%;
	max-width: 860px;
	border-radius: 24px;
	overflow: hidden;
	box-shadow: 0 14px 30px rgba(0, 0, 0, 0.12);
	background: #ffffff;
}

.module-card-cover {
	position: relative;
	height: 300px;
	background: linear-gradient(135deg, #ffb3ba, #b8c6ff);
	background-size: cover;
	background-position: center;
}

.module-card-cover__overlay {
	position: absolute;
	inset: 0;
	background: linear-gradient(180deg, rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.3));
}

.module-manage-btn {
	position: absolute;
	top: 16px;
	right: 16px;
	background: #ffffff;
	border: none;
	border-radius: 12px;
	padding: 10px 18px;
	font-family: 'Poppins', Helvetica;
	font-weight: 600;
	color: #1f2a44;
	cursor: pointer;
	box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
	transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.module-manage-btn:hover {
	transform: translateY(-2px);
	box-shadow: 0 10px 18px rgba(0, 0, 0, 0.2);
}

.module-card-title {
	padding: 20px 28px;
	font-family: 'Poppins', Helvetica;
	font-size: 20px;
	font-weight: 700;
	color: #1f2a44;
}

.module-tabs {
	display: flex;
	gap: 12px;
	flex-wrap: wrap;
}

.module-tab {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 10px 18px;
	border-radius: 14px;
	border: 1px solid #e5e7eb;
	background: #ffffff;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 600;
	color: #5b6475;
	cursor: pointer;
	transition: all 0.2s ease;
}

.module-tab--active {
	background: #2b6af3;
	border-color: #2b6af3;
	color: #ffffff;
	box-shadow: 0 8px 18px rgba(43, 106, 243, 0.3);
}

.module-tab__icon {
	font-size: 16px;
}

.module-tab__icon svg {
	width: 18px;
	height: 18px;
	display: block;
}

.module-tab-content {
	background: #ffffff;
	border-radius: 18px;
	padding: 24px;
	box-shadow: 0 10px 20px rgba(0, 0, 0, 0.06);
}

.panel-empty {
	font-family: 'Poppins', Helvetica;
	color: #94a3b8;
	font-size: 14px;
	text-align: center;
	padding: 40px 0;
}

.panel-cards {
	display: grid;
	gap: 20px;
	grid-template-columns: repeat(3, minmax(0, 1fr));
}

.module-item-card {
	border-radius: 14px;
	padding: 18px 20px;
	border: 1px solid rgba(15, 23, 42, 0.08);
	box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
	backdrop-filter: blur(6px);
	transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
	position: relative;
	padding-bottom: 44px;
}

.module-item-card--homework {
	background: linear-gradient(135deg, #f7d7e1 0%, #d6e1ff 100%);
}

.module-item-card--test {
	background: linear-gradient(135deg, #ffe8d6 0%, #dff0ff 100%);
}

.module-item-card:hover {
	transform: translateY(-2px);
	box-shadow: 0 14px 28px rgba(15, 23, 42, 0.12);
	border-color: rgba(37, 99, 235, 0.2);
}

.module-item-card__header {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 12px;
}

.module-item-card__title {
	margin: 0;
	font-family: 'Poppins', Helvetica;
	font-size: 15px;
	font-weight: 700;
	color: #1f2a44;
}

.module-item-card__link {
	font-family: 'Poppins', Helvetica;
	font-size: 13px;
	color: #3b82f6;
	text-decoration: underline;
	white-space: nowrap;
}

.module-item-card__meta {
	display: flex;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 10px 18px;
	margin-top: 12px;
	font-family: 'Poppins', Helvetica;
	font-size: 12.5px;
	color: #1f2937;
}

.module-item-card__submitted {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 4px 10px;
	border-radius: 999px;
	background: rgba(15, 118, 110, 0.12);
	color: #0f766e;
	font-weight: 700;
	letter-spacing: 0.1px;
	position: absolute;
	right: 16px;
	bottom: 14px;
}

.module-item-card__link {
	text-decoration: none;
	font-weight: 600;
	padding: 6px 14px;
	border-radius: 999px;
	background: rgba(0, 149, 236, 0.12);
	color: #0095ec;
	border: 1px solid rgba(0, 149, 236, 0.2);
	transition: all 0.2s ease;
}

.module-item-card__link:hover {
	background: rgba(0, 149, 236, 0.2);
	color: #0077be;
}

.module-item-card__meta--grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.module-item-card__meta--pairs {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 10px 18px;
}

.module-item-card__meta--bottom {
	display: flex;
	justify-content: space-between;
	align-items: flex-end;
	gap: 12px;
}

.module-item-card__actions {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-top: 16px;
	gap: 12px;
	flex-wrap: wrap;
}

.module-item-card__toggle {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	background: transparent;
	border: none;
	font-family: 'Poppins', Helvetica;
	font-weight: 600;
	color: #6b7280;
	cursor: pointer;
}

.module-item-card__eye {
	font-size: 16px;
}

@media (max-width: 768px) {
	.panel-cards {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	.module-card-cover {
		height: 220px;
	}

	.module-card-title {
		font-size: 18px;
	}

	.module-item-card__meta {
		flex-direction: column;
		align-items: flex-start;
	}
}

@media (max-width: 520px) {
	.panel-cards {
		grid-template-columns: 1fr;
	}
}
</style>
